/** @file Reads IK constraints, sockets and the compiled update order of a rig. */
import type { Pose2DJson } from '@pose2d/format/types';
import type { IkData, SocketData, UpdateOp } from '../../skeletonData';
import { coreFail } from '../coreFail';
import { indexIn } from './indexIn';

/**
 * IK (1 or 2 bones; bend omitted = positive, mix 1, softness 0, flags false), sockets (grip2Bone -1) and the update
 * order. An op kind other than `b`/`ik` (a newer runtime's `tc`/`path`/`phys`, §5.12) is VALIDATION, never IK.
 */
export function readRigConstraints(json: Pose2DJson, boneCount: number): { ik: IkData[]; sockets: SocketData[]; updateOrder: UpdateOp[] } {
  const ik = json.ik.map((c, i): IkData => {
    if (c.bones.length < 1 || c.bones.length > 2) coreFail('VALIDATION', `IK needs 1 or 2 bones, got ${c.bones.length}`, { path: `ik[${i}].bones` });
    return {
      name: c.name,
      bones: c.bones.map((b, k) => indexIn(b, boneCount, `ik[${i}].bones[${k}]`)),
      target: indexIn(c.target, boneCount, `ik[${i}].target`),
      mix: c.mix ?? 1,
      softness: c.softness ?? 0,
      bendPositive: c.bend !== -1,
      compress: c.compress ?? false,
      stretch: c.stretch ?? false,
      uniform: c.uniform ?? false,
    };
  });
  const sockets = json.sockets.map((s, i): SocketData => ({
    name: s.name,
    bone: indexIn(s.bone, boneCount, `sockets[${i}].bone`),
    length: s.length,
    grip2Bone: s.grip2Bone === undefined ? -1 : indexIn(s.grip2Bone, boneCount, `sockets[${i}].grip2Bone`),
  }));
  const updateOrder = json.updateOrder.map(([kind, index], i): UpdateOp => {
    const path = `updateOrder[${i}]`;
    const op: string = kind;
    if (op === 'b') return ['b', indexIn(index, boneCount, path)];
    if (op === 'ik') return ['ik', indexIn(index, ik.length, path)];
    return coreFail('VALIDATION', `unknown update op "${op}"`, { path, hint: 'the rig needs a newer Pose2D runtime' });
  });
  return { ik, sockets, updateOrder };
}
